import { useMemo } from 'react';
import { Search, X } from 'lucide-react';
import { Input } from '@ui/input';
import { Button } from '@ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@ui/select';
import type { PunchlistFilters } from '../types';

interface PunchListsFiltersProps {
  filters: PunchlistFilters;
  onFiltersChange: (filters: PunchlistFilters) => void;
  totalCount: number;
  filteredCount: number;
  hideTypeFilter?: boolean;
}

const DEFAULT_FILTERS: PunchlistFilters = {
  search: '',
  status: 'all',
  type: 'all',
  sortBy: 'updated_at',
  sortOrder: 'desc',
};

const PunchListsFilters = ({
  filters,
  onFiltersChange,
  totalCount,
  filteredCount,
  hideTypeFilter = false,
}: PunchListsFiltersProps) => {
  const hasActiveFilters = useMemo(
    () =>
      filters.search !== '' ||
      filters.status !== 'all' ||
      (!hideTypeFilter && filters.type !== 'all'),
    [filters, hideTypeFilter]
  );

  const update = (patch: Partial<PunchlistFilters>) =>
    onFiltersChange({ ...filters, ...patch });

  return (
    <div className="flex items-center gap-3 flex-wrap">
      <div className="relative">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="Search punch lists..."
          value={filters.search}
          onChange={(e) => update({ search: e.target.value })}
          className="pl-8 w-[220px]"
        />
      </div>

      <Select
        value={filters.status}
        onValueChange={(value) =>
          update({ status: value as PunchlistFilters['status'] })
        }
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          <SelectItem value="completed">Completed</SelectItem>
          <SelectItem value="in_progress">In Progress</SelectItem>
        </SelectContent>
      </Select>

      {!hideTypeFilter && (
        <Select
          value={filters.type}
          onValueChange={(value) =>
            update({ type: value as PunchlistFilters['type'] })
          }
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            <SelectItem value="template">Templates</SelectItem>
            <SelectItem value="instance">Instances</SelectItem>
          </SelectContent>
        </Select>
      )}

      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() =>
            onFiltersChange({
              ...DEFAULT_FILTERS,
              type: hideTypeFilter ? filters.type : DEFAULT_FILTERS.type,
            })
          }
          className="gap-1"
        >
          <X className="h-4 w-4" />
          Clear Filters
        </Button>
      )}

      <span className="ml-auto text-sm text-muted-foreground">
        Showing {filteredCount} of {totalCount}
      </span>
    </div>
  );
};

export default PunchListsFilters;
